import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from '@interacta-shared/data-access-configuration';
import { deleteApp, initializeApp } from 'firebase/app';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { getDatabase } from 'firebase/database';
import { from, map, mergeMap, Observable, of } from 'rxjs';
import { toFirebaseOptions } from '../models/firebase-channel/firebase-channel.deserialize';
import { FirebaseChannelsClientTransport } from '../models/firebase-channel/firebase-channel.model';

@Injectable({ providedIn: 'root' })
export class FirebaseTransportService {
    private readonly http = inject(HttpClient);

    private readonly authBasePath = inject(ENVIRONMENT).apiBasePath.auth;
    private transport: FirebaseChannelsClientTransport | null = null;
    private firebaseAppNameDiscriminator = 1;

    initializeChannelClientTransport(): Observable<FirebaseChannelsClientTransport> {
        if (this.transport) {
            return of(this.transport);
        } else {
            return this.http
                .post<{
                    clientTransport: {
                        transportParams: {
                            firebaseAuthToken: string;
                        };
                    };
                }>(
                    `${this.authBasePath}/create-push-channels-client-transport`,
                    null,
                )
                .pipe(
                    map((res) => ({
                        firebaseOptions: toFirebaseOptions(
                            res.clientTransport.transportParams,
                        ),
                        firebaseAuthToken:
                            res.clientTransport.transportParams
                                .firebaseAuthToken,
                    })),
                    mergeMap((clientTransportData) => {
                        const firebaseAppName = `pushChannelsClientTransport-${this
                            .firebaseAppNameDiscriminator++}-${
                            clientTransportData.firebaseOptions.projectId ?? ''
                        }`;
                        const firebaseApp = initializeApp(
                            clientTransportData.firebaseOptions,
                            firebaseAppName,
                        );

                        const transport = {
                            id:
                                clientTransportData.firebaseOptions.projectId ??
                                '',
                            firebaseApp: firebaseApp,
                            firebaseDB: getDatabase(firebaseApp),
                        };

                        this.transport = transport;

                        const auth = getAuth(firebaseApp);

                        return from(
                            signInWithCustomToken(
                                auth,
                                clientTransportData.firebaseAuthToken,
                            ),
                        ).pipe(map(() => transport));
                    }),
                );
        }
    }

    async closeChannelClientTransport(): Promise<void> {
        if (this.transport) {
            await deleteApp(this.transport.firebaseApp);
            this.transport = null;
        }
    }
}
